import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { campaignCanvasserApi } from '../lib/api'
import { readGrantMirror, writeGrantMirror } from '../lib/grantMirror'
import { readOrganizerCanvass, writeOrganizerCanvass } from '../lib/organizerCanvass'
import { getPendingCalls, getPendingVisits, getRouteOutcomes } from '../lib/offlineStore'
import { syncEngine } from '../lib/syncEngine'
import { storeGrantSessionToken } from '../lib/session/grantSessionToken'
import type { CanvassThisResultDto } from '../types/grant'

export type CanvassThisStart = 'terms' | 'blocked' | 'ready'

/**
 * [Canvass this] The organizer's "Canvass this" tap: CT-13 pre-flight (switching campaigns never
 * wipes), the POST, then the CT-14 success effects. Never touches auth_token or the offline store.
 */
export function useCanvassThis(campaignId: string) {
  const qc = useQueryClient()
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null)
  const [termsVersion, setTermsVersion] = useState<string | null>(null)

  const mutation = useMutation<CanvassThisResultDto, unknown, { acceptTerms: boolean; termsVersion?: string }>({
    mutationFn: async (body) => (await campaignCanvasserApi.canvassThis(campaignId, body)).data,
  })
  // useMutation returns a fresh object every render — depend on the stable mutateAsync only.
  const m = mutation.mutateAsync

  const start = useCallback(async (acceptTerms = false): Promise<CanvassThisStart> => {
    setBlockedMessage(null)

    // [CT-13 / R3] Decided per queued visit: visits carry no grant id and sync under whichever
    // grant_session cookie is current, so any non-rejected visit for another campaign must drain
    // before the new grant takes over. Rejected entries are terminal and never block.
    const otherCampaignVisits = async () =>
      (await getPendingVisits())
        .filter((r) => r.entry.rejected !== true && r.entry.visit.campaignId !== campaignId)
        .map((r) => r.entry.visit.campaignId)
    // [R5] Queued drop-offs carry the grant that recorded them (legacy unstamped ones = the current
    // mirror's grant). Any unsynced, non-rejected one for a grant other than this campaign's known
    // grant blocks the same way: after the switch it would drain as the wrong grant and be rejected.
    const targetGrantId = (() => {
      const marker = readOrganizerCanvass()
      const mirror = readGrantMirror()
      if (marker?.campaignId === campaignId) return marker.grantId
      if (mirror?.campaign?.id === campaignId) return mirror.grantId
      return null
    })()
    const otherGrantOutcomes = async () => {
      const current = readGrantMirror()?.grantId
      return (await getRouteOutcomes())
        .filter((o) => !o.synced && o.rejected !== true)
        .map((o) => o.grantId ?? current ?? null)
        .filter((g) => g === null || g !== targetGrantId)
    }
    // [CF-1 P4 / R4] Queued calls carry their grant the same way; another grant's unsynced, non-rejected
    // calls block the switch too (they are never wiped, and would be rejected under the new grant).
    const otherGrantCalls = async () => {
      const current = readGrantMirror()?.grantId
      return (await getPendingCalls())
        .filter((c) => c.entry.rejected !== true)
        .map((c) => c.entry.grantId ?? current ?? null)
        .filter((g) => g === null || g !== targetGrantId)
    }
    let others = await otherCampaignVisits()
    let otherOutcomes = await otherGrantOutcomes()
    let otherCalls = await otherGrantCalls()
    if ((others.length > 0 || otherOutcomes.length > 0 || otherCalls.length > 0) && navigator.onLine) {
      try {
        await syncEngine.sync()
      } catch {
        // best-effort — the re-read below decides
      }
      others = await otherCampaignVisits()
      otherOutcomes = await otherGrantOutcomes()
      otherCalls = await otherGrantCalls()
    }
    if (others.length > 0) {
      const marker = readOrganizerCanvass()
      const mirror = readGrantMirror()
      const fromId = others[0]
      const fromLabel =
        (marker?.campaignId === fromId ? marker.campaignLabel : '') ||
        (mirror?.campaign?.id === fromId ? mirror.campaign.label : '') ||
        'another campaign'
      setBlockedMessage(
        `${others.length} visits from ${fromLabel} haven't synced yet. Get online and open that campaign's walk to sync them before canvassing here.`,
      )
      return 'blocked'
    }
    if (otherOutcomes.length > 0) {
      const marker = readOrganizerCanvass()
      const mirror = readGrantMirror()
      const fromGrant = otherOutcomes[0]
      const fromLabel =
        (fromGrant && marker?.grantId === fromGrant ? marker.campaignLabel : '') ||
        (fromGrant && mirror?.grantId === fromGrant ? mirror.campaign?.label ?? '' : '') ||
        'another campaign'
      setBlockedMessage(
        `${otherOutcomes.length} drop-offs from ${fromLabel} haven't synced yet. Get online and open that campaign's route to sync them before canvassing here.`,
      )
      return 'blocked'
    }
    if (otherCalls.length > 0) {
      const marker = readOrganizerCanvass()
      const mirror = readGrantMirror()
      const fromGrant = otherCalls[0]
      const fromLabel =
        (fromGrant && marker?.grantId === fromGrant ? marker.campaignLabel : '') ||
        (fromGrant && mirror?.grantId === fromGrant ? mirror.campaign?.label ?? '' : '') ||
        'another campaign'
      setBlockedMessage(
        `${otherCalls.length === 1 ? '1 call' : `${otherCalls.length} calls`} from ${fromLabel} ${otherCalls.length === 1 ? "hasn't" : "haven't"} synced yet. Get online and open that campaign's call list to sync them before canvassing here.`,
      )
      return 'blocked'
    }

    const result = await m({
      acceptTerms,
      termsVersion: acceptTerms ? termsVersion ?? undefined : undefined,
    })
    if (result.termsRequired) {
      setTermsVersion(result.termsVersion)
      return 'terms'
    }
    if (!result.identity) throw new Error('Canvass this returned no identity')

    // [CT-14] Same mirror shape as the redeem/enroll success path; the campaign here is id + label only.
    const { identity } = result
    const campaign = result.campaign ?? identity.campaign ?? null
    // [Native B1, R9] A native od_ caller gets the minted gs_ in the body (no cookie): store it
    // before anything else, so /grant/me and the /canvassing posts carry the GRANT credential.
    await storeGrantSessionToken(identity)
    writeGrantMirror({
      grantId: identity.grantId,
      kind: identity.kind,
      subjectContactId: identity.subjectContactId,
      ownerCommitteeId: identity.ownerCommitteeId,
      campaign: campaign ? { id: campaign.id, label: campaign.label } : null,
      poc: identity.poc ?? null,
    })
    writeOrganizerCanvass({
      grantId: identity.grantId,
      campaignId: campaign?.id ?? campaignId,
      campaignLabel: campaign?.label || '',
    })
    void qc.invalidateQueries({ queryKey: ['campaigns', campaignId, 'available-canvassers'] })
    void qc.invalidateQueries({ queryKey: ['campaigns', campaignId, 'canvasser-grants'] })
    void qc.invalidateQueries({ queryKey: ['grant', 'me'] })
    return 'ready'
  }, [m, qc, campaignId, termsVersion])

  return {
    start,
    blockedMessage,
    termsVersion,
    isPending: mutation.isPending,
    error: mutation.error as unknown,
  }
}
